import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOrder, OrderResponse, updateOrder, type UpdateOrderPayload, requestCustomerApproval, fetchLatestCustomerApprovalRequest } from '../../../api/orders';
import Notification from '../../../components/midone/components/Base/Notification';
import type { NotificationElement } from '../../../components/midone/components/Base/Notification';
import { fetchCurrentUser, type UserProfile } from '../../../api/auth';

interface OrderCustomerApprovalProps {
  orderId: string | null;
  showHeader?: boolean;
  onBackToOrders?: () => void;
  onReturnToIntake?: (orderId: string | null) => void;
  onCompleted?: (orderId: string) => void;
}

type ToastState = {
  id: number;
  title: string;
  message: string;
};

const TOAST_DURATION = 2500;

const formatDateTime = (value: string | null) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString();
};

const getUserDisplayName = (user?: UserProfile | null) => {
  if (!user) {
    return 'Unknown user';
  }

  const name = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (name) {
    return name;
  }

  return user.username || 'Unknown user';
};

const OrderCustomerApproval: FC<OrderCustomerApprovalProps> = ({
  orderId,
  showHeader = true,
  onCompleted
}) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [customerApproved, setCustomerApproved] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastRef = useRef<NotificationElement | null>(null);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [lastRequestedAt, setLastRequestedAt] = useState<string | null>(null);
  const [lastRequestExpiresAt, setLastRequestExpiresAt] = useState<string | null>(null);
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetchCurrentUser()
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setCurrentUser(response.user);
      })
      .catch(() => {
        if (isMounted) {
          setCurrentUser(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setCustomerApproved(false);
      setCustomerNotes('');
      setOrderError('Order ID not specified.');
      setLoadingOrder(false);
      setLastRequestedAt(null);
      setLastRequestExpiresAt(null);
      return;
    }

    let isMounted = true;
    setLoadingOrder(true);
    setOrderError(null);

    fetchOrder(orderId)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setOrder(data);
        setCustomerApproved(data.customerApproved ?? false);
        setCustomerNotes(data.customerNotes ?? '');
        setSaveError(null);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load order details.';
        setOrder(null);
        setCustomerApproved(false);
        setCustomerNotes('');
        setOrderError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoadingOrder(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [orderId]);

  // Load latest customer approval request timestamps
  useEffect(() => {
    if (!orderId) return;
    let active = true;
    fetchLatestCustomerApprovalRequest(orderId)
      .then((res) => {
        if (!active) return;
        if (res.ok && res.data) {
          setLastRequestedAt(res.data.createdAt);
          setLastRequestExpiresAt(res.data.expiresAt);
        } else {
          setLastRequestedAt(null);
          setLastRequestExpiresAt(null);
        }
      })
      .catch(() => {
        if (!active) return;
        setLastRequestedAt(null);
        setLastRequestExpiresAt(null);
      });
    return () => { active = false; };
  }, [orderId]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    toastRef.current?.showToast?.();

    const handle = window.setTimeout(() => {
      toastRef.current?.hideToast?.();
      setToast(null);
    }, TOAST_DURATION);

    return () => {
      window.clearTimeout(handle);
      toastRef.current?.hideToast?.();
    };
  }, [toast]);

  const hasValidApprovalRequest = useMemo(() => {
    if (!lastRequestExpiresAt) return false;
    const exp = new Date(lastRequestExpiresAt);
    if (Number.isNaN(exp.getTime())) return false;
    return exp.getTime() > Date.now();
  }, [lastRequestExpiresAt]);

  const normalizedNotes = useMemo(() => customerNotes.trim(), [customerNotes]);
  const initialNotes = useMemo(() => (order?.customerNotes ?? '').trim(), [order]);

  const dirty = useMemo(() => {
    if (!order) {
      return false;
    }

    const approvalChanged = customerApproved !== (order.customerApproved ?? false);
    const notesChanged = normalizedNotes !== initialNotes;
    return approvalChanged || notesChanged;
  }, [order, customerApproved, normalizedNotes, initialNotes]);

  const approvalInfo = useMemo(() => {
    if (!order) {
      return 'No approval recorded yet.';
    }

    if (!order.customerApprovedAt) {
      return 'No approval recorded yet.';
    }

    const approverName = getUserDisplayName(order.customerApprovedBy ?? order.createdBy ?? null);
    return `Recorded on ${formatDateTime(order.customerApprovedAt)} by ${approverName}.`;
  }, [order]);

  const handleSave = async () => {
    if (!orderId || !order) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload: UpdateOrderPayload = {
      customerApproved,
      customerNotes: normalizedNotes.length ? normalizedNotes : null,
    };

    if (customerApproved) {
      const approverId =
        currentUser?.id ?? order.customerApprovedById ?? order.createdById ?? null;

      if (approverId) {
        payload.customerApprovedById = approverId;
      }
    } else if (order.customerApprovedById) {
      payload.customerApprovedById = null;
    }

    try {
      const updated = await updateOrder(orderId, payload);
      setOrder(updated);
      setCustomerApproved(updated.customerApproved ?? false);
      setCustomerNotes(updated.customerNotes ?? '');
      setToast({
        id: Date.now(),
        title: 'Saved',
        message: 'Customer approval has been updated.',
      });
      onCompleted && onCompleted(orderId);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message || 'Failed to update customer approval.'
          : 'Failed to update customer approval.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const approvalBadgeClass = customerApproved
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : 'bg-slate-200 text-slate-600 border border-slate-300';

  return (
    <div className="space-y-6">
      <Notification
        className="flex w-80 max-w-full flex-col items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-center text-primary shadow-lg"
        options={{
          duration: TOAST_DURATION,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true,
          close: false,
          offset: {
            x: 24,
            y: 24,
          },
        }}
        getRef={(el: NotificationElement) => {
          toastRef.current = el;
        }}
      >
        {toast ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">{toast.title}</p>
            <p className="text-xs text-primary/80">{toast.message}</p>
          </div>
        ) : null}
      </Notification>

      {showHeader ? (
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-slate-800">Customer approval</h2>
          <p className="text-sm text-slate-500">
            Record the customer&apos;s consent for this order and capture any verbal notes.
          </p>
        </div>
      ) : null}

      <div className="flex items-center justify-end">
        <button
          type="button"
          className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:border-emerald-400 hover:bg-emerald-100 disabled:opacity-60"
          onClick={async () => {
            if (!orderId) return;
            try {
              setRequestingApproval(true);
              const res = await requestCustomerApproval(orderId);
              if (res.ok) {
                setToast({ id: Date.now(), title: 'Request sent', message: 'OTP valid for 30 minutes.' });
                setLastRequestedAt(new Date().toISOString());
                if (res.expiresAt) setLastRequestExpiresAt(res.expiresAt);
              } else {
                setSaveError(res.error || 'Failed to request approval');
              }
            } catch (e) {
              setSaveError(e instanceof Error ? e.message : 'Failed to request approval');
            } finally {
              setRequestingApproval(false);
            }
          }}
          disabled={saving || requestingApproval || !orderId}
        >
          {requestingApproval ? 'Requesting…' : 'Request Customer Approval'}
        </button>
      </div>

      <div className="mt-1 text-right text-xs text-slate-500">
        {lastRequestedAt ? (
          hasValidApprovalRequest ? (
            <span>
              Last requested: {formatDateTime(lastRequestedAt)} • Expires: {formatDateTime(lastRequestExpiresAt)}
            </span>
          ) : (
            <span>
              Last request expired at {formatDateTime(lastRequestExpiresAt)}. Please request again.
            </span>
          )
        ) : (
          <span>No approval request sent.</span>
        )}
      </div>

      {orderId && hasValidApprovalRequest ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Customer approval link</p>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={`${window.location.origin}/customer/order-approval/${orderId}`}
              className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-primary/30 hover:text-primary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`${window.location.origin}/customer/order-approval/${orderId}`);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                } catch {
                  /* ignore */
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Send this link to the customer to review and approve their estimate.</p>
        </div>
      ) : null}

      {orderError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">{orderError}</div>
      ) : null}

      <div className="rounded-2xl  bg-white p-6 shadow-sm">
        {loadingOrder ? (
          <p className="text-sm text-slate-500">Loading order details…</p>
        ) : !order ? (
          <p className="text-sm text-slate-500">Select an order to manage customer approval.</p>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Order number</p>
                <p className="text-lg font-semibold text-slate-800">{order.orderNumber}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${approvalBadgeClass}`}>
                {customerApproved ? 'Approved' : 'Pending'}
                {dirty ? ' • Unsaved' : ''}
              </span>
            </div>

            <div className='mt-2'>
              <label className="inline-flex items-center gap-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={customerApproved}
                  onChange={(event) => setCustomerApproved(event.target.checked)}
                  disabled={saving}
                />
                Customer approval received
              </label>
              <p className="mt-1 text-xs text-slate-500">
                Enable once the customer (or the assigned sales advisor on their behalf) confirms the estimate.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Customer notes</label>
              <textarea
                value={customerNotes}
                onChange={(event) => setCustomerNotes(event.target.value)}
                placeholder="Add any notes shared by the customer regarding this approval."
                rows={4}
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
                disabled={saving}
              />
              <p className="mt-1 text-xs text-slate-400">These notes are stored on the order for future reference.</p>
            </div>

            <div
              className={`rounded-xl border px-4 py-3 text-xs ${
                order?.customerApproved
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-slate-50 text-slate-500'
              }`}
            >
              <p>{approvalInfo}</p>
              {dirty ? (
                <p className="mt-1 text-amber-600">
                  You have unsaved changes. Save to update the approval log.
                </p>
              ) : null}
            </div>

            {saveError ? (
              <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{saveError}</div>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCustomerApproval;
